import { useMemo } from 'react';

import { AppIcons } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerQuestion } from '@backrs_ui/grow-ui/src/models/models';

const STRINGS = appStrings.modules.survey.output.aspirationCategories;

export type AspirationCategory = Readonly<{
  key: string;
  name: string;
  iconName: AppIcons;
  calculatorKeys: string[];
  questions: CareerQuestion[];
}>;

type CategoryDefinition = Readonly<{
  key: string;
  name: string;
  iconName: AppIcons;
  calculatorKeys: string[];
}>;

const CATEGORY_DEFINITIONS: CategoryDefinition[] = [
  {
    key: 'lifestyle_and_home',
    name: STRINGS.lifestyleAndHome,
    iconName: 'Home03',
    calculatorKeys: ['future_home', 'future_lifestyle'],
  },
  {
    key: 'locations_and_options',
    name: STRINGS.locationsAndOptions,
    iconName: 'Globe05',
    calculatorKeys: ['surrounding_location', 'job_or_career_options'],
  },
  {
    key: 'family_and_caregiving',
    name: STRINGS.familyAndCaregiving,
    iconName: 'Users02',
    calculatorKeys: ['future_family', 'childrens_education', 'childcare', 'caregiver_flexibility'],
  },
  {
    key: 'work_settings',
    name: STRINGS.workSettingsTradeoff,
    iconName: 'Building06',
    calculatorKeys: ['preferred_work_environments', 'work_environments_to_avoid'],
  },
  {
    key: 'career_attributes',
    name: STRINGS.careerAttributes,
    iconName: 'Paperclip',
    calculatorKeys: ['professional_growth', 'professional_recognition'],
  },
  {
    key: 'building_wealth',
    name: STRINGS.buildingWealth,
    iconName: 'CoinsStacked02',
    calculatorKeys: ['building_wealth'],
  },
  {
    key: 'sources_of_meaning',
    name: STRINGS.sourcesOfMeaning,
    iconName: 'Scales01',
    calculatorKeys: ['sources_of_meaning'],
  },
  {
    key: 'activities',
    name: STRINGS.activities,
    iconName: 'GamingPad02',
    calculatorKeys: ['activities_you_enjoy', 'activities_you_dont_like'],
  },
];

export function useAspirationCategories(careerQuestions: CareerQuestion[]): AspirationCategory[] {
  return useMemo(() => {
    return CATEGORY_DEFINITIONS.map((def) => {
      const questions = def.calculatorKeys.flatMap((key) =>
        careerQuestions.filter((q) => q.calculator_key === key),
      );

      // Deduplicate in case a question appears in multiple calculator_keys
      const seen = new Set<number>();
      const uniqueQuestions = questions.filter((q) => {
        if (seen.has(q.id)) return false;

        seen.add(q.id);

        return true;
      });

      return {
        key: def.key,
        name: def.name,
        iconName: def.iconName,
        calculatorKeys: def.calculatorKeys,
        questions: uniqueQuestions,
      };
    }).filter((category) => category.questions.length > 0);
  }, [careerQuestions]);
}
